import {FormControl, FormControlLabel, InputLabel, MenuItem, Stack, Switch} from "@mui/material";
import CustomSelect from "../../../components/formValidation/CustomSelect";
import {deliveryFormats, deliveryPeriods, deliveryTypes} from "../../../utils/enums";
import React, {useEffect, useState} from "react";
import ErrorHandlingForm from "../../../components/formValidation/ErrorHandlingFrom";
import useDataModel from "../../../hooks/dataModel";
import useCallDataApi from "../../../hooks/data";
import CustomDatePicker from "../../../components/formValidation/CustomDatePicker";
import CustomTimePicker from "../../../components/formValidation/CustomTimePicker";
import {
    validateMaxDateDiff,
    validateRequired,
    validateRequiredIf,
    validateTime
} from "../../../components/formValidation/validators";
import CustomAddressPicker from "../../../components/formValidation/CustomAddressPicker";
import CustomSwitch from "../../../components/formValidation/CustomSwitch";
import CustomTextField from "../../../components/formValidation/CustomTextField";

const DeliveryData = ({data, setData, reference}) => {
    const [onlyHungary, setOnlyHungary] = useState(true)
    const {fetchModel: fetchUser, models: couriers, setModels: setCouriers} = useDataModel(useCallDataApi('users'))

    useEffect(() => fetchUser('get_couriers', models => setCouriers(models)), [])

    const [errors, setErrors] = useState({
        delivery_format: false,
        delivery_address: false,
        delivery_type: false,
        delivery_courier_new: false,
        delivery_time: false,
        delivery_period: false,
        actual_delivery_date: false,
        delivery_time_set: false,
        delivery_comment: false,
    })
    const validators = {
        delivery_format: [],
        delivery_address: [value => validateRequiredIf(value?.address_com, data?.delivery_type !== deliveryTypes.TGS_OFFICE)],
        delivery_type: [validateRequired],
        delivery_courier_new: [],
        delivery_time: [validateTime],
        delivery_period: [],
        actual_delivery_date: [validateRequired, value => validateMaxDateDiff(10, value, data?.delivery_date)],
        delivery_time_set: [],
        delivery_comment: [],
    }

    const onAddressChange = (address) => {
        const country = address?.address?.country
        const postalCode = address?.address?.postal_code
        const hungarian = country === 'HU'
        const number = parseInt(postalCode)
        const inBudapest = 1000 <= number && number < 2000
        // console.log(`Address changing, hungary: ${hungarian} | budapest: ${inBudapest} | postal code: ${number}`)
        if (hungarian) {
            if (inBudapest) {
                setData(prev => ({...prev, delivery_type: deliveryTypes.HAND}))
            } else {
                setData(prev => ({...prev, delivery_type: deliveryTypes.GLS}))
            }
        } else {
            setData(prev => ({...prev, delivery_type: deliveryTypes.DHL}))
        }
    }

    const onDeliveryMethodChange = method => {
        if ([deliveryTypes.GLS, deliveryTypes.DHL].includes(method)) {
            setData(prev => ({...prev, delivery_format: deliveryFormats.Case}))
        } else setData(prev => ({...prev, delivery_format: deliveryFormats.Backpack}))
    }

    useEffect(() => {
        onDeliveryMethodChange(data?.delivery_type)
    }, [data?.delivery_type])

    return <ErrorHandlingForm
        data={data}
        setData={setData}
        errors={errors}
        setErrors={setErrors}
        validators={validators}
        reference={reference}
    >
        <Stack spacing={1}>
            <FormControl sx={{width: '100%'}}>
                <InputLabel id="delivery-format">Kiszállítási formátum</InputLabel>
                <CustomSelect
                    labelId="delivery-format"
                    label="Delivery Format"
                    name="delivery_format"
                    data={data}
                    setData={setData}
                    errors={errors}
                    setErrors={setErrors}
                    validators={validators}
                >
                    <MenuItem value=''>-</MenuItem>
                    {Object.entries(deliveryFormats).map(([key, value]) => <MenuItem key={key}
                                                                                     value={value}>{value}</MenuItem>)}
                </CustomSelect>
            </FormControl>

            <FormControl sx={{width: '100%'}}>
                <InputLabel id="delivery-types">Szállítási mód</InputLabel>
                <CustomSelect
                    labelId="delivery-types"
                    label="Szállítási mód"
                    name="delivery_type"
                    data={data}
                    setData={setData}
                    errors={errors}
                    setErrors={setErrors}
                    validators={validators}
                    sideEffect={deliveryType => onDeliveryMethodChange(deliveryType)}
                >
                    <MenuItem value=''>-</MenuItem>
                    {Object.entries(deliveryTypes).map(([key, value]) => <MenuItem key={key}
                                                                                   value={value}>{value}</MenuItem>)}
                </CustomSelect>
            </FormControl>

            {data?.delivery_type === deliveryTypes.HAND && <FormControl sx={{width: '100%'}}>
                <InputLabel id="delivery-courier">Kiszállító futár</InputLabel>
                <CustomSelect
                    labelId="delivery-courier"
                    label="Kiszállító futár"
                    name="delivery_courier_new"
                    data={data}
                    setData={setData}
                    errors={errors}
                    setErrors={setErrors}
                    validators={validators}
                >
                    <MenuItem value={null}>-</MenuItem>
                    {couriers.map(r => (
                        <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                    ))}
                </CustomSelect>
            </FormControl>}

            {data?.delivery_type !== deliveryTypes.TGS_OFFICE && <FormControlLabel
                control={<Switch checked={onlyHungary} onChange={() => setOnlyHungary(prev => !prev)}/>}
                label={onlyHungary ? "Csak magyar címek" : "Bármilyen cím"}
            />}

            {data?.delivery_type !== deliveryTypes.TGS_OFFICE && <CustomAddressPicker
                onlyHungary={onlyHungary}
                name='delivery_address'
                label="Szállítási cím"
                data={data}
                setData={setData}
                errors={errors}
                setErrors={setErrors}
                validators={validators}
                onAddressChange={address => onAddressChange(address)}
            />}

            <CustomSwitch
                name='delivery_time_set'
                labelOn='Pontos időt adok meg'
                labelOff='Intervallumot adok meg'
                data={data}
                setData={setData}
                errors={errors}
                setErrors={setErrors}
                validators={validators}
            />

            {data?.delivery_time_set && <CustomTimePicker
                name='delivery_time'
                label="Szállítási idő"
                sx={{width: '100%'}}
                data={data}
                setData={setData}
                errors={errors}
                setErrors={setErrors}
                validators={validators}
            />}

            {!data?.delivery_time_set && <FormControl sx={{width: '100%'}}>
                <InputLabel id="deivery_period_selector">Oda mikor</InputLabel>
                <CustomSelect
                    labelId="deivery_period_selector"
                    label="Mikor"
                    name="delivery_period"
                    data={data}
                    setData={setData}
                    errors={errors}
                    setErrors={setErrors}
                    validators={validators}
                >
                    <MenuItem value=''>-</MenuItem>
                    {Object.entries(deliveryPeriods).map(([key, value]) => <MenuItem key={key}
                                                                                     value={value}>{value}</MenuItem>)}
                </CustomSelect>
            </FormControl>}


            <CustomDatePicker
                sx={{width: '100%'}}
                name="actual_delivery_date"
                label="Kiszállítás dátuma"
                data={data}
                setData={setData}
                errors={errors}
                setErrors={setErrors}
                validators={validators}
                maxDate={data?.delivery_date}
                inputFormat='yyyy-MM-dd'
            />

            <CustomTextField
                name='delivery_comment'
                label="Kiszállítás komment"
                multiline={true}
                rows={4}
                data={data}
                setData={setData}
                errors={errors}
                setErrors={setErrors}
                validators={validators}
            />

        </Stack>
    </ErrorHandlingForm>
}

export default DeliveryData